import React, { useState, useMemo } from "react";
import { Search, Plus, Minus, Mail, ArrowRight } from "lucide-react";
import useAuth from "../hooks/useAuth";

const AccordionItem = ({ question, answer, isOpen, toggle }) => (
  <div
    className="border-b border-gray-100 last:border-0 py-5 cursor-pointer group"
    onClick={toggle}
  >
    <div className="flex items-center justify-between select-none">
      <h3
        className={`text-base font-medium transition-colors duration-200 ${isOpen
            ? "text-emerald-800"
            : "text-gray-800 group-hover:text-emerald-700"
          }`}
      >
        {question}
      </h3>
      <div
        className={`p-1 rounded-full transition-colors duration-200 ${isOpen
            ? "bg-emerald-50 text-emerald-600"
            : "text-gray-400 group-hover:text-emerald-500"
          }`}
      >
        {isOpen ? <Minus size={16} /> : <Plus size={16} />}
      </div>
    </div>
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? "max-h-96 opacity-100 mt-4" : "max-h-0 opacity-0"
        }`}
    >
      <p className="text-sm text-gray-600 leading-relaxed pr-8 pb-2">
        {answer}
      </p>
    </div>
  </div>
);

export default function ApprovalPortalHelp() {
  const { auth } = useAuth();
  const perms = auth?.permissions || [];

  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [openIndex, setOpenIndex] = useState(null);

  const helpData = useMemo(
    () => [
      {
        id: "general",
        label: "General",
        permissions: [],
        items: [
          {
            q: "Accessing the portal",
            a: "The portal uses Single Sign-On (SSO). Use your official organizational email credentials. No separate registration is required.",
          },
          {
            q: "Dashboard visibility",
            a: "Your dashboard view is determined automatically by your role. If you don't see specific charts or metrics, you likely do not have the permissions required to view that data.",
          },
          {
            q: "Updating contact details",
            a: "While your name and department are synced from the central HR database, you can update your notification phone number in the Profile settings.",
          },
        ],
      },
      {
        id: "approvals",
        label: "Approvals",
        permissions: ["verify", "approve", "accept", "all", "admin"],
        items: [
          {
            q: "Verification vs. Approval",
            a: "Verification checks document validity. Approval authorizes the request. Both steps must be completed before an acceptance letter can be generated.",
          },
          {
            q: "Issuing acceptance letters",
            a: "Navigate to the 'Acceptance' tab. Locate candidates in the 'Pending' list and click 'Process'. The system generates and emails the letter automatically.",
          },
          {
            q: "Rejecting a verified application",
            a: "Yes, an Approver can reject an application even if it was previously verified. This final decision overrides the verification status.",
          },
        ],
      },
      {
        id: "management",
        label: "Management",
        permissions: [
          "supervise",
          "manage",
          "assign_supervisor",
          "all",
          "admin",
        ],
        items: [
          {
            q: "Assigning supervisors",
            a: "Go to 'Deployments'. Select unassigned interns and choose a supervisor from the directory. The supervisor will receive an immediate notification.",
          },
          {
            q: "Endorsing logbooks",
            a: "In the 'Endorsements' section, review the daily/weekly logs submitted by your interns. Click 'Endorse' to digitally sign them for payment processing.",
          },
          {
            q: "Payment advice generation",
            a: "Payment advice is calculated automatically based on attendance and endorsed logbooks. You can view the final report in the 'Payment Advice' module.",
          },
        ],
      },
      {
        id: "security",
        label: "Security",
        permissions: ["security_view", "all", "admin"],
        items: [
          {
            q: "Gate pass verification",
            a: "Enter the intern's ID in the 'Security Checks' search bar. The system will display their active status and permitted access zones.",
          },
          {
            q: "Handling flagged personnel",
            a: "If an intern is marked 'Flagged' in red, deny entry immediately and refer them to the administrative office for clearance.",
          },
        ],
      },
      {
        id: "admin",
        label: "Admin",
        permissions: ["all", "admin"],
        items: [
          {
            q: "Managing system roles",
            a: "Navigate to 'Users'. Search for a staff member and toggle permissions (e.g., Verify, Approve, Security View) as needed.",
          },
          {
            q: "Closing vacancies",
            a: "Go to 'Vacancies'. Toggle the status of any active position to 'Closed'. This stops new applications but allows processing of existing ones.",
          },
        ],
      },
    ],
    []
  );

  const visibleCategories = useMemo(() => {
    return helpData.filter((cat) => {
      if (cat.permissions.length === 0) return true;
      return cat.permissions.some((p) => perms.includes(p));
    });
  }, [helpData, perms]);

  const displayedContent = useMemo(() => {
    if (searchTerm) {
      const allItems = visibleCategories.flatMap((cat) => cat.items);
      return allItems.filter(
        (item) =>
          item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.a.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (activeCategory === "all") {
      return visibleCategories.flatMap((cat) => cat.items);
    }
    return (
      visibleCategories.find((cat) => cat.id === activeCategory)?.items || []
    );
  }, [searchTerm, activeCategory, visibleCategories]);

  return (
    <div className="min-h-screen bg-white pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-3xl font-semibold text-gray-900 mb-4 tracking-tight">
            How can we help?
          </h1>
          <div className="relative max-w-lg mx-auto">
            <input
              type="text"
              placeholder="Search for answers..."
              className="w-full pl-12 pr-4 py-3 bg-gray-50 border-none rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
        </div>

        {!searchTerm && (
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${activeCategory === "all"
                  ? "bg-emerald-600 text-white shadow-md shadow-emerald-200"
                  : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                }`}
            >
              All Topics
            </button>
            {visibleCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${activeCategory === cat.id
                    ? "bg-emerald-600 text-white shadow-md shadow-emerald-200"
                    : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                  }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        <div className="bg-white">
          {displayedContent.length > 0 ? (
            <div className="space-y-1">
              {displayedContent.map((item, idx) => (
                <AccordionItem
                  key={idx}
                  question={item.q}
                  answer={item.a}
                  isOpen={openIndex === idx}
                  toggle={() => setOpenIndex(openIndex === idx ? null : idx)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">
                No results found matching your criteria.
              </p>
              <button
                onClick={() => setSearchTerm("")}
                className="mt-2 text-emerald-600 text-sm font-medium hover:underline"
              >
                Clear search
              </button>
            </div>
          )}
        </div>

        <div className="mt-20 pt-10 border-t border-gray-100">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-6 bg-gray-50 rounded-2xl border border-gray-100">
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">
                Still need support?
              </h3>
              <p className="text-sm text-gray-500">
                Our team is available Mon-Fri, 8am - 5pm.
              </p>
            </div>
            <a
              href="mailto:support@nnpc.group"
              className="group flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:border-emerald-200 hover:text-emerald-700 transition-all shadow-sm hover:shadow"
            >
              <Mail size={16} />
              <span>Contact IT Support</span>
              <ArrowRight
                size={14}
                className="opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all"
              />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
